import { z } from 'zod';
import { batchContextSchema, externalIdSchema, rrwebEventSchema } from './recording.js';
/**
 * Contrat d'ingestion des Exceptions : une erreur JavaScript survenue chez un visiteur, envoyée
 * par le SDK (voir `CONTEXT.md`). Distinct de celui des enregistrements : l'API répond à chaque
 * Exception, et un `console.error` n'en est pas une.
 *
 * Une Exception est identifiée par son `uuid` : la renvoyer (retry) ne la compte qu'une fois.
 */
/** Chemin de l'endpoint d'ingestion des Exceptions, relatif à l'hôte de l'API. */
export const EXCEPTIONS_PATH = '/ingest/exceptions';
/** Taille maximale d'une Exception envoyée, décompressée. */
export const MAX_EXCEPTION_BYTES = 256 * 1024;
/** Frames gardés au plus par Exception (les plus proches de l'erreur). */
export const MAX_EXCEPTION_FRAMES = 50;
/** Longueur maximale du message d'une Exception. */
export const MAX_EXCEPTION_MESSAGE_LENGTH = 10_000;
/** Occurrences écartées au plus, déclarées par une Exception (`duplicateCount`). */
export const MAX_DUPLICATE_COUNT = 1_000_000;
/** Longueur maximale du type d'une Exception (`TypeError`…). */
export const MAX_EXCEPTION_TYPE_LENGTH = 256;
/**
 * Type donné aux valeurs jetées qui ne sont pas des `Error` (chaîne, objet, `undefined`…) :
 * leur message est la valeur sérialisée, sans frames.
 */
export const NON_ERROR_TYPE = 'Error';
/** Origine d'une Exception : un gestionnaire de la page, ou un appel de l'application. */
export const EXCEPTION_MECHANISMS = ['onerror', 'unhandledrejection', 'captureException'];
/** Un appel de la pile, du plus proche de l'erreur au plus lointain. */
export const exceptionFrameSchema = z.strictObject({
    function: z.string().max(512).optional(),
    /** URL du script (ou chemin tel que donné par le navigateur). */
    file: z.string().max(4096).optional(),
    line: z.number().int().nonnegative().optional(),
    column: z.number().int().nonnegative().optional(),
});
/** Contexte de la page, comme celui des batchs d'enregistrement (sans URL : celle de l'Exception). */
export const exceptionContextSchema = z.strictObject({
    userAgent: z.string().max(1024),
    locale: z.string().max(64),
    timezone: z.string().max(64),
    viewport: z.strictObject({
        width: z.number().int().nonnegative(),
        height: z.number().int().nonnegative(),
    }),
});
export const exceptionReportSchema = z
    .strictObject({
    /** Clé publique du projet (`pk_…`), dans le corps pour rester compatible avec `keepalive`. */
    publicKey: z.string().regex(/^pk_[A-Za-z0-9_-]{1,100}$/),
    sdkVersion: z.string().min(1).max(32),
    /** Identifiant du navigateur (UUIDv7). */
    distinctId: z.uuid(),
    /**
     * Utilisateur à qui appartient le navigateur, s'il est identifié (`identify()`) : l'API lui
     * rattache le `distinctId`, comme un `$identify`. Indispensable en Mode erreurs, qui n'envoie
     * aucun batch d'enregistrement.
     */
    userId: externalIdSchema.optional(),
    /** Identifiant de l'Exception (UUIDv7), pour le dédoublonnage des renvois. */
    uuid: z.uuid(),
    /** Instant de l'Exception, en ms depuis l'epoch. */
    timestamp: z.number().int().nonnegative(),
    /** URL de la page, paramètres sensibles masqués. */
    url: z.string().max(4096),
    context: exceptionContextSchema,
    type: z.string().min(1).max(MAX_EXCEPTION_TYPE_LENGTH),
    message: z.string().max(MAX_EXCEPTION_MESSAGE_LENGTH),
    frames: z.array(exceptionFrameSchema).max(MAX_EXCEPTION_FRAMES),
    /** Erreur attrapée par l'application (`captureException`) ; sinon, non gérée. */
    handled: z.boolean(),
    mechanism: z.enum(EXCEPTION_MECHANISMS),
    /**
     * Occurrences identiques écartées par le dédoublonnage du SDK depuis l'envoi précédent de cette
     * erreur (absent : aucune). Une erreur qui boucle part au plus une fois toutes les 10 s.
     */
    duplicateCount: z.number().int().min(1).max(MAX_DUPLICATE_COUNT).optional(),
    /** Session complète en cours dans l'onglet (absente hors session). */
    session: z.strictObject({ id: z.uuid(), windowId: z.uuid() }).optional(),
    /**
     * Extrait du Recording qui accompagne l'Exception, en Mode erreurs (UUIDv7 généré par le SDK) :
     * ses batchs partent à `EXTRAITS_PATH`. Jamais avec une session.
     */
    extraitId: z.uuid().optional(),
})
    .refine((report) => !(report.session && report.extraitId), {
    message: 'Une Exception référence une session ou un Extrait, pas les deux.',
    path: ['extraitId'],
});
/** Chemin de l'endpoint d'ingestion des Extraits, relatif à l'hôte de l'API. */
export const EXTRAITS_PATH = '/ingest/extraits';
/** Volume maximal d'un Extrait, décompressé, tous batchs confondus (413 `extrait_too_large`). */
export const MAX_EXTRAIT_BYTES = 20 * 1024 * 1024;
/** Code d'erreur de la réponse 413 : le SDK abandonne l'Extrait. */
export const EXTRAIT_TOO_LARGE = 'extrait_too_large';
/**
 * Un batch d'Extrait (voir `CONTEXT.md`) : le morceau de Recording envoyé en Mode erreurs autour
 * d'une Exception, découpé comme les enregistrements. Il est identifié par `(extraitId, seq)` :
 * le renvoyer ne change rien. Le premier batch commence par un full snapshot.
 */
export const extraitBatchSchema = z.strictObject({
    publicKey: z.string().regex(/^pk_[A-Za-z0-9_-]{1,100}$/),
    sdkVersion: z.string().min(1).max(32),
    distinctId: z.uuid(),
    extraitId: z.uuid(),
    seq: z.number().int().min(0).max(10_000),
    context: batchContextSchema,
    events: z.array(rrwebEventSchema).min(1),
});
